import React, { useEffect, useState, memo } from "react";
import { Button, Col, Form, Select, Input, Radio, Row, RadioChangeEvent, Tag, notification, Badge } from "antd";
import { CoffeeOutlined, LoadingOutlined } from "@ant-design/icons";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { useQueryClient } from "@tanstack/react-query";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import UserService from "@Services/UserService";
import SessionManager from "@Core/session";
import { IUserConnectionDetails, IConnectionRequestDetails } from "@Models/IUserConnectionDetails";
import ConnectionItem from "@Components/connections/ConnectionItem";
import { SkeletonConnectionItem } from "@Components/connections/SkeletonConnectionItem";
import ActivityList, { IGridLayout } from "@Components/activity/ActivityList";
import { QueryKeys } from "@Constants";
import "@Styles/pages/connectionsPage.scss";
import PageHelmet from "@Components/shared/PageHelmet";
import { useConnectionsQuery } from "@Components/shared/queries";
import { useSearchPotentialConnections } from "./queries";
import NotificationService from "@Services/NotificationService";

interface ConnectionsFilter {
    status: "connected" | "received" | "sent";
    keyWord: string;
}

interface IFormValues {
    users: string[];
}

const ConnectionsPage: React.FC = () => {
    const [isSending, setSending] = useState(false);
    const [keyWord, setKeyWord] = useState("");
    const [connectionCounts, setConnectionCounts] = useState({
        connected: 0,
        received: 0,
        sent: 0,
    });
    const [filteredConnections, setFilteredConnections] = useState<IUserConnectionDetails[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [filter, setFilter] = useState<ConnectionsFilter>({ status: "connected", keyWord: "" });
    const queryClient = useQueryClient();

    const { isFetching, data } = useConnectionsQuery();
    const { userId } = SessionManager.getServiceUser();

    const [connectionForm] = Form.useForm();

    /**
     * Return a user tag to be rendered in the Select box.
     * @param props
     */
    const renderUserTag = (props: CustomTagProps) => {
        const { label, closable, onClose } = props;
        const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <Tag
                color={"blue"}
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
            >
                {label}
            </Tag>
        );
    };

    /**
     * Search the platform for other users to connect with, based on a minimum of three characters
     */
    const { data: options = [], isSearching } = useSearchPotentialConnections(encodeURIComponent(keyWord));

    const validUserOptions = () =>
        options.filter((option) => data.findIndex((x) => x.userId === option.value) === -1 && option.value !== userId);

    const handleKeywordSearch = (value: string) => {
        if (value.length < 3) return;
        setKeyWord(value);
    };

    /**
     * Send connection request to users selected in the search field.
     * @param values
     */
    const onSubmitConnectionForm = async (values: IFormValues) => {
        setSending(true);
        const model: IConnectionRequestDetails = {
            users: values.users?.slice().map((id) => ({ id })),
        };
        const service = new UserService();
        await service.sendConnectionRequest(userId, model);
        notification.success({ message: "Connection request sent." });
        setKeyWord("");
        setSending(false);
        setSelectedUsers([]);
        connectionForm.resetFields(["users"]);
        await queryClient.invalidateQueries([QueryKeys.UserConnections]);
    };

    /**
     * Accept a connection request.
     * @param id
     */
    const onAcceptConnectionRequest = async (id: string) => {
        const service = new UserService();
        await service.acceptConnectionRequest(SessionManager.getServiceUser()?.userId, id);
        await queryClient.invalidateQueries([QueryKeys.UserConnections]);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Decline a connection request.
     * @param id
     */
    const onDeclineConnectionRequest = async (id: string) => {
        const service = new UserService();
        await service.refuseConnectionRequest(SessionManager.getServiceUser()?.userId, id);
        await queryClient.invalidateQueries([QueryKeys.UserConnections]);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Remove a connection.
     * @param id
     */
    const onRemoveConnection = async (connectionId: string, userId: string) => {
        const service = new UserService();
        await service.deleteConnection(SessionManager.getServiceUser()?.userId, connectionId);

        //delete corresponding notification if it exists
        const notifService = new NotificationService();
        const result = await notifService.getNotifications(SessionManager.getServiceUser()?.userId);
        const notification = result?.value.find(
            (item) => item.payload.category === "connection" && item.payload.entityId === userId
        );
        if (notification) {
            await notifService.setNotificationDeleted(notification.notificationId);
        }
        await queryClient.invalidateQueries([QueryKeys.UserConnections]);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Withdraw a connection request made by the logged in user.
     * @param id
     */
    const onCancelConnectionRequest = async (id: string) => {
        const service = new UserService();
        await service.cancelConnectionRequest(SessionManager.getServiceUser()?.userId, id);
        await queryClient.invalidateQueries([QueryKeys.UserConnections]);
        await queryClient.invalidateQueries([QueryKeys.Notifications]);
    };

    /**
     * Update the filter
     * @param e
     */
    const onFilterChange = (e: RadioChangeEvent) => {
        setFilter((prevState) => ({ ...prevState, status: e.target.value }));
    };

    /**
     * Update the filter when the keyword filter field is changed
     * @param e
     */
    const onFilterKeyWordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setFilter((prevState) => ({ ...prevState, keyWord: value }));
    };

    /**
     * Apply filtering to connections - options are 'connected' | 'received' | 'sent'
     */
    useEffect(() => {
        const result = data
            ?.slice()
            .filter((item) => item.status === filter.status)
            .filter((item) => {
                const nickName = item.nickName?.toLowerCase();
                const keyword = filter.keyWord.toLowerCase();

                return (
                    item.fullName.toLowerCase().indexOf(keyword) !== -1 ||
                    (nickName && nickName.indexOf(keyword) !== -1)
                );
            });
        setFilteredConnections(result);
    }, [filter, data]);

    useEffect(() => {
        const result = {
            connected: 0,
            received: 0,
            sent: 0,
        };
        for (const i in data) {
            const status = data[i].status;
            result[status] += 1;
        }
        setConnectionCounts(result);
    }, [data]);

    const gridLayout: IGridLayout = { gutter: 5, xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 };

    return (
        <article className="connections-page">
            <PageHelmet name={"Connections"} />
            <Row gutter={[8, 8]} justify="space-between">
                <Col span={8} lg={8} md={24} xs={24}>
                    <div>
                        <Input
                            placeholder="Filter by name"
                            allowClear
                            onChange={onFilterKeyWordChange}
                            className="connections-search"
                        />
                    </div>
                    <div style={{ margin: "20px 0px" }}>
                        <Radio.Group
                            className="connection-radio newUI-raised"
                            onChange={onFilterChange}
                            defaultValue="connected"
                        >
                            <Radio.Button value="connected">
                                Connected
                                <Badge
                                    title={"Connected: " + connectionCounts.connected}
                                    className="connection-count"
                                    count={connectionCounts.connected}
                                    style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                                />
                            </Radio.Button>
                            <Radio.Button value="received">
                                Received
                                <Badge
                                    title={"Connected: " + connectionCounts.received}
                                    className="connection-count"
                                    count={connectionCounts.received}
                                    style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                                />
                            </Radio.Button>
                            <Radio.Button value="sent">
                                Sent
                                <Badge
                                    title={"Connected: " + connectionCounts.sent}
                                    className="connection-count"
                                    count={connectionCounts.sent}
                                    style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                                />
                            </Radio.Button>
                        </Radio.Group>
                    </div>
                </Col>
                <Col span={16} lg={16} md={24} xs={24}>
                    <Form
                        name="connection-form"
                        className="connection-form"
                        form={connectionForm}
                        onFinish={(values: IFormValues) => onSubmitConnectionForm(values)}
                    >
                        <Form.Item name="users" className="users">
                            <Select
                                allowClear={true}
                                placeholder={"Type a name or email to find users to connect with"}
                                filterOption={false} // let the BE handle the filtering
                                showArrow
                                mode="multiple"
                                // loading in the box while it's searching | otherwise it shows results or "no data"
                                notFoundContent={
                                    isSearching ? <LoadingOutlined style={{ fontSize: 16 }} spin /> : undefined
                                }
                                tagRender={renderUserTag}
                                options={validUserOptions()}
                                maxTagCount={20}
                                onSearch={AwesomeDebouncePromise(handleKeywordSearch, 800)}
                                onChange={(value) => setSelectedUsers(value)}
                                value={selectedUsers.length > 0 ? selectedUsers : null}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button
                                loading={isSending}
                                disabled={selectedUsers.length === 0}
                                icon={<CoffeeOutlined />}
                                className="secondary"
                                htmlType="submit"
                            >
                                Send Connection Request
                            </Button>
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
            <Row gutter={[8, 8]} justify="space-between">
                <Col span={8} lg={8} md={24} xs={24}>
                    {isFetching && (!data || data.length === 0) ? (
                        <Row gutter={[16, 16]} justify="space-between">
                            <SkeletonConnectionItem />
                            <SkeletonConnectionItem />
                        </Row>
                    ) : (
                        <Row gutter={[32, 32]}>
                            {filteredConnections?.map((connection) => (
                                <Col span={24} key={connection.userId} className="connection-item-col">
                                    <ConnectionItem
                                        onAccept={onAcceptConnectionRequest}
                                        onDecline={onDeclineConnectionRequest}
                                        onRemove={onRemoveConnection}
                                        onCancel={onCancelConnectionRequest}
                                        {...connection}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </Col>
                <Col span={16} lg={16} md={24} xs={24}>
                    {!isFetching && (
                        <ActivityList
                            userIds={data
                                ?.filter((connection) => connection.status === "connected")
                                .slice(0, 50)
                                ?.map((x) => x.userId)}
                            queryKey={QueryKeys.ActivityListConnections}
                            gridLayout={gridLayout}
                        />
                    )}
                </Col>
            </Row>
        </article>
    );
};

export default memo(ConnectionsPage);
